import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { useAuth } from "@/hooks/use-auth";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { AuthRoute } from "@/components/auth/auth-route";
import { LabellerHomeRedirect, LabellerRoute } from "@/components/auth/labeller-route";
import { PlatformAdminRoute } from "@/components/auth/platform-admin-route";
import { AppLayout } from "@/components/layout/app-layout";
import LoginPage from "@/pages/login";
import LabellerWorkstationPage from "@/pages/labelling/workstation";
import WaitingPage from "@/pages/waiting";
import DashboardPage from "@/pages/dashboard";
import InventoryPage from "@/pages/inventory";
import AllRecipesPage from "@/pages/menus/all-recipes";
import RecipePage from "@/pages/menus/recipe-page";
import MenusPage from "@/pages/menus/menus-page";
import MenusArchivePage from "@/pages/menus/menus-archive";
import MenuDetailPage from "@/pages/menus/menu-detail";
import DefaultMenuPage from "@/pages/menus/default";
import WasteTransactionsPage from "@/pages/waste/transactions";
import WasteEventDetailPage from "@/pages/waste/event-detail";
import WasteSummaryPage from "@/pages/waste/summary";
import ReportsPage from "@/pages/reports";

/**
 * The app's first lazy route (POS2-1137 CP2). Everything else above is a static import.
 *
 * The report document plus its four layouts is most of POS2-1137's weight and none of it is needed
 * by a user who never opens a report, so it is loaded when one is opened. The main bundle is
 * measured against a 40 kB gzip cap and the service worker against a 3 MiB precache ceiling
 * `vite.config.ts:62` records having already been raised once for a ~2.2 MB bundle.
 */
const ReportPreviewPage = lazy(() => import("@/pages/reports/preview"));
import WasteExplorePage from "@/pages/waste/explore";
import OccupancyPage from "@/pages/occupancy";
import AdminOrganisationsPage from "@/pages/admin/organisations";
import AdminUsersPage from "@/pages/admin/users";
import AdminSsoPage from "@/pages/admin/sso";
import AdminCataloguesPage from "@/pages/admin/catalogues";
import AdminDefaultMenuPage from "@/pages/admin/default-menu";
import AdminDataBridgePage from "@/pages/admin/databridge";
import AdminDevicesPage from "@/pages/admin/devices";
import AdminSitesPage from "@/pages/admin/sites";
import AdminMonitoringPage from "@/pages/admin/monitoring";
import AdminLabellingPage from "@/pages/admin/labelling";
import AdminActivityPage from "@/pages/admin/activity";
import AdminCustomerHealthPage from "@/pages/admin/customer-health";
import AdminTranslationsPage from "@/pages/admin/translations";
import SettingsUsersPage from "@/pages/settings/users";
import SettingsAccountsPage from "@/pages/settings/accounts";
import SettingsSitesPage from "@/pages/settings/sites";
import SettingsKitchensPage from "@/pages/settings/kitchens";
import SettingsDevicesPage from "@/pages/settings/devices";
import SettingsOrganisationPage from "@/pages/settings/organisation";
import SettingsCataloguePage from "@/pages/settings/catalogue";
import AcceptInvitationPage from "@/pages/invite/accept";
import ConfirmEmailPage from "@/pages/auth/confirm";
import MfaChallengePage from "@/pages/mfa-challenge";
import AccountProfilePage from "@/pages/account";
import AccountSecurityPage from "@/pages/settings/security";
import ForgotPasswordPage from "@/pages/forgot-password";
import ResetPasswordPage from "@/pages/reset-password";

export default function App() {
  useAuth();

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/waiting" element={<AuthRoute />}>
          <Route index element={<WaitingPage />} />
        </Route>
        <Route path="/mfa-challenge" element={<MfaChallengePage />} />
        <Route path="/invite/accept" element={<AcceptInvitationPage />} />
        <Route path="/auth/confirm" element={<ConfirmEmailPage />} />
        <Route element={<ProtectedRoute />}>
          {/* Chromeless — a sibling of AppLayout, not a child of it. A labeller
              sees only the workstation, so there is no shell to render around
              it. Nothing but the auth pages sat outside AppLayout before this
              (Kitchen Display is a mode inside the dashboard page, not a route). */}
          <Route element={<LabellerRoute />}>
            <Route path="labelling" element={<LabellerWorkstationPage />} />
          </Route>
          <Route element={<LabellerHomeRedirect />}>
            <Route element={<AppLayout />}>
              <Route index element={<DashboardPage />} />
              {/* Reports stays under Waste (POS2-1137, reversing pinned decision 1 at Artem's
                  request): the nav item lives in the Waste group and nowhere else, so the surface
                  is not doubled. `/reports` is the redirect now, which keeps any link written
                  while the route was top-level working. */}
              <Route path="reports" element={<Navigate to="/waste/reports" replace />} />
              <Route path="stock" element={<InventoryPage />} />
              <Route path="inventory" element={<Navigate to="/stock" replace />} />
              <Route path="menus/recipes" element={<AllRecipesPage />} />
              <Route path="menus/recipes/new" element={<RecipePage />} />
              <Route path="menus/recipes/:id" element={<RecipePage />} />
              <Route path="menus" element={<MenusPage />} />
              <Route path="menus/archive" element={<MenusArchivePage />} />
              <Route path="menus/default" element={<DefaultMenuPage />} />
              <Route path="menus/:id" element={<MenuDetailPage />} />
              <Route path="menus/cycles" element={<Navigate to="/menus" replace />} />
              <Route path="menus/cycles/:id" element={<Navigate to="/menus" replace />} />
              <Route path="menus/upload" element={<Navigate to="/menus" replace />} />
              <Route path="menus/uploads/:id/review" element={<Navigate to="/menus" replace />} />
              <Route path="waste/transactions" element={<WasteTransactionsPage />} />
              <Route path="waste/events/:id" element={<WasteEventDetailPage />} />
              <Route path="waste/reports" element={<ReportsPage />} />
              <Route
                path="waste/reports/:reportKey"
                element={
                  // The one Suspense boundary in the app, and it is here rather than around the
                  // layout on purpose: the chrome is already rendered when the chunk is asked for,
                  // so only the panel it fills waits.
                  <Suspense fallback={<div className="p-6" />}>
                    <ReportPreviewPage />
                  </Suspense>
                }
              />
              <Route path="waste/summary" element={<WasteSummaryPage />} />
              <Route path="waste/explore" element={<WasteExplorePage />} />
              <Route path="waste/explorer" element={<Navigate to="/waste/explore" replace />} />
              <Route path="occupancy" element={<OccupancyPage />} />
              <Route path="settings/users" element={<SettingsUsersPage />} />
              <Route path="settings/organisation" element={<SettingsOrganisationPage />} />
              <Route path="settings/accounts" element={<SettingsAccountsPage />} />
              <Route path="settings/catalogue" element={<SettingsCataloguePage />} />
              <Route path="settings/sites" element={<SettingsSitesPage />} />
              <Route path="settings/kitchens" element={<SettingsKitchensPage />} />
              <Route path="settings/devices" element={<SettingsDevicesPage />} />
              <Route path="account" element={<AccountProfilePage />} />
              <Route path="account/security" element={<AccountSecurityPage />} />
              <Route path="admin" element={<PlatformAdminRoute />}>
                <Route path="organisations" element={<AdminOrganisationsPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="sso" element={<AdminSsoPage />} />
                <Route path="catalogues" element={<AdminCataloguesPage />} />
                <Route path="default-menu" element={<AdminDefaultMenuPage />} />
                <Route path="databridge" element={<AdminDataBridgePage />} />
                <Route path="devices" element={<AdminDevicesPage />} />
                <Route path="sites" element={<AdminSitesPage />} />
                <Route path="monitoring" element={<AdminMonitoringPage />} />
                <Route path="labelling" element={<AdminLabellingPage />} />
                <Route path="activity" element={<AdminActivityPage />} />
                <Route path="customer-health" element={<AdminCustomerHealthPage />} />
                <Route path="translations" element={<AdminTranslationsPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
